import { router } from "@inertiajs/react"
import { ReceiptText } from "lucide-react"

import { AgentStateBadge } from "@/components/agents/agent-state-badge"
import { AgentMark, markForSlug } from "@/components/ui/agent-mark"
import { Empty } from "@/components/ui/empty"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { partnerAccountPath } from "@/routes"

import type { AccountAgent, BillingData } from "./types"

function fmt(cents: number) {
  return "$" + Math.round(cents / 100).toLocaleString()
}

// IMP-511: the account's jobs, absorbed from the retired Agents tab — what
// the client is paying for, with the state kept visible (the partner is the
// support contact). Cards with the isometric marks, echoing the client app's
// Jobs page.
function JobsList({ agents }: { agents: AccountAgent[] }) {
  const jobs = agents.filter((a) => a.state !== "retired")
  if (jobs.length === 0) return null

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {jobs.map((agent) => (
        <div
          key={agent.uid}
          className="bg-card rounded-lg border p-4 shadow-xs"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg">
              <AgentMark
                kind={markForSlug(agent.archetype_slug)}
                className="text-foreground size-7"
              />
            </div>
            <AgentStateBadge state={agent.state} />
          </div>
          <p className="mt-4 text-[15px]/6 font-medium">{agent.name}</p>
          <p className="text-muted-foreground mt-1 text-sm tabular-nums">
            {agent.retail_price_cents == null
              ? "—"
              : `${fmt(agent.retail_price_cents)}/mo`}
          </p>
        </div>
      ))}
    </div>
  )
}

export function BillingTab({
  billing,
  agents,
  accountUid,
}: {
  billing: BillingData
  agents: AccountAgent[]
  accountUid: string
}) {
  const { periods, statement } = billing
  const selectedUid =
    periods.find((p) => p.label === statement?.label)?.uid ??
    periods[0]?.uid ??
    ""

  function handlePeriodChange(uid: string) {
    router.visit(partnerAccountPath({ id: accountUid }), {
      data: { billing_period: uid },
      preserveState: true,
      preserveScroll: true,
    })
  }

  if (!statement) {
    return (
      <div className="flex flex-col gap-10">
        <JobsList agents={agents} />
        <Empty
          icon={<ReceiptText className="h-5 w-5" />}
          title="No billing data"
          description="No billing data is available for this account yet."
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-10">
      <JobsList agents={agents} />
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">
            Statement — {statement.label}
          </h2>
          {periods.length > 1 && (
            <Select value={selectedUid} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p.uid} value={p.uid}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="text-foreground min-w-full text-left text-sm/6">
            <thead className="text-muted-foreground">
              <tr>
                <th className="border-b px-4 py-2 font-medium first:pl-1">
                  Agent
                </th>
                <th className="border-b px-4 py-2 font-medium">Period</th>
                <th className="border-b px-4 py-2 pr-1 text-right font-medium">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {statement.line_items.map((item) => (
                <tr key={item.uid} className="hover:bg-muted/50">
                  <td className="border-b px-4 py-4 font-medium first:pl-1">
                    {item.agent_name}
                  </td>
                  <td className="text-muted-foreground border-b px-4 py-4">
                    <span>{item.period_label}</span>
                    {item.prorated && item.active_days && item.period_days && (
                      <span className="mt-0.5 block text-xs">
                        Prorated &middot; {item.active_days} of{" "}
                        {item.period_days} days
                      </span>
                    )}
                    {!item.prorated && (
                      <span className="mt-0.5 block text-xs">Full month</span>
                    )}
                  </td>
                  <td className="border-b px-4 py-4 pr-1 text-right font-medium tabular-nums">
                    {fmt(item.retail_price_cents ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t">
                <td colSpan={2} className="px-4 py-4 font-semibold first:pl-1">
                  Total
                </td>
                <td className="px-4 py-4 pr-1 text-right font-semibold tabular-nums">
                  {fmt(statement.total_retail_cents)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}
